/**
 * Hook orquestrador para sessão de Screen Share (Lado do Widget/Cliente)
 * Coordena signaling, WebRTC, captura de tela e estado da sessão
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import { useScreenShare } from './useScreenShare';
import { useWebRTC } from './useWebRTC';
import { useWebRTCSignaling } from './useWebRTCSignaling';
import type { 
  ScreenShareSessionState, 
  RequestPayload, 
  OfferPayload, 
  IceCandidatePayload,
  EndPayload 
} from './types';

// Logger para diagnóstico
const logger = createLogger('useClientScreenShare');

/**
 * Props do hook
 */
interface UseClientScreenShareProps {
  /** ID da conversa atual */
  conversationId: string | null;
  /** Se true, habilita a funcionalidade */
  enabled?: boolean;
}

/**
 * Retorno do hook
 */
interface UseClientScreenShareReturn {
  /** Estado atual da sessão */
  sessionState: ScreenShareSessionState;
  /** Dados da solicitação recebida (quando pendente) */
  pendingRequest: RequestPayload | null;
  /** Se true, está compartilhando tela */
  isSharing: boolean;
  /** Aceita a solicitação de visualização */
  acceptRequest: () => void;
  /** Recusa a solicitação de visualização */
  rejectRequest: (reason?: string) => void;
  /** Encerra sessão/compartilhamento ativo */
  endSession: () => void;
  /** Mensagem de erro, se houver */
  error: string | null;
}

/**
 * Hook para gerenciar sessão de screen share do lado do CLIENTE (Widget)
 * 
 * Fluxo:
 * 1. Recebe request do atendente → exibe modal de consentimento
 * 2. Se aceito, envia accept e aguarda offer
 * 3. Recebe offer, inicia captura de tela (getDisplayMedia)
 * 4. Adiciona stream à conexão e envia answer
 * 5. Mantém compartilhamento até encerrar
 */
export function useClientScreenShare(props: UseClientScreenShareProps): UseClientScreenShareReturn {
  const { conversationId, enabled = true } = props;

  // Estado da sessão
  const [sessionState, setSessionState] = useState<ScreenShareSessionState>('idle');
  const [pendingRequest, setPendingRequest] = useState<RequestPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Ref para ICE candidates pendentes
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  // Hook de captura de tela
  const screenShare = useScreenShare();

  // Handler para ICE candidates locais
  const handleLocalIceCandidate = useCallback((candidate: RTCIceCandidateInit) => {
    signaling.sendIceCandidate(candidate, 'client');
  }, []);

  // Handler para mudança de estado da conexão
  const handleConnectionStateChange = useCallback((state: RTCPeerConnectionState) => {
    logger.debug('Estado da conexão mudou', { state });
    
    if (state === 'connected') {
      setSessionState('connected');
      setError(null);
    } else if (state === 'failed') {
      screenShare.stopSharing();
      setSessionState('ended');
      setError('Falha na conexão P2P');
    } else if (state === 'disconnected') {
      setSessionState('reconnecting');
    }
  }, [screenShare]);

  // Hook WebRTC
  const webrtc = useWebRTC({
    onIceCandidate: handleLocalIceCandidate,
    onConnectionStateChange: handleConnectionStateChange,
  });

  // Handlers de signaling
  const handleRequest = useCallback((data: RequestPayload) => {
    logger.info('Recebeu solicitação de visualização', { agentName: data.agentName });
    setPendingRequest(data);
    setSessionState('pending');
    setError(null);
  }, []);

  const handleOffer = useCallback(async (data: OfferPayload) => {
    logger.info('Recebeu offer do atendente');
    
    try {
      // Inicia captura de tela (abre dialog nativo do navegador)
      // startSharing retorna o MediaStream diretamente para evitar race condition
      const capturedStream = await screenShare.startSharing();
      if (!capturedStream) {
        logger.warn('Usuário cancelou captura de tela');
        signaling.sendEnd('client', 'user_action');
        setSessionState('ended');
        setError('Captura de tela cancelada');
        return;
      }

      // Adiciona stream capturado diretamente à conexão WebRTC
      // Usa capturedStream ao invés de screenShare.stream para evitar race condition
      webrtc.addLocalStream(capturedStream);

      // Cria e envia answer
      const answer = await webrtc.createAnswer(data.sdp);
      signaling.sendAnswer(answer);
      
      // Processa ICE candidates pendentes
      for (const candidate of pendingIceCandidatesRef.current) {
        await webrtc.addIceCandidate(candidate);
      }
      pendingIceCandidatesRef.current = [];
      
      setSessionState('connecting');
    } catch (err) {
      logger.error('Erro ao processar offer', err);
      setError('Erro ao iniciar compartilhamento');
      signaling.sendEnd('client', 'error');
      setSessionState('ended');
    }
  }, [screenShare, webrtc]);

  const handleIce = useCallback(async (data: IceCandidatePayload) => {
    // Ignora ICE candidates próprios
    if (data.from === 'client') return;
    
    logger.debug('Recebeu ICE candidate do atendente');
    
    // Se ainda não definiu remote description, guarda para depois
    if (webrtc.connectionState === 'new') {
      pendingIceCandidatesRef.current.push(data.candidate);
      return;
    }
    
    try {
      await webrtc.addIceCandidate(data.candidate);
    } catch (err) {
      logger.warn('Erro ao adicionar ICE candidate', err);
    }
  }, [webrtc]);

  const handleEnd = useCallback((data: EndPayload) => {
    logger.info('Sessão encerrada pelo atendente', { reason: data.reason });
    screenShare.stopSharing();
    webrtc.close();
    setSessionState('ended');
    setPendingRequest(null);
  }, [screenShare, webrtc]);

  // Hook de signaling
  const signaling = useWebRTCSignaling({
    conversationId,
    enabled: enabled && !!conversationId,
    handlers: {
      onRequest: handleRequest,
      onOffer: handleOffer,
      onIce: handleIce,
      onEnd: handleEnd,
    },
  });

  // Aceita solicitação
  const acceptRequest = useCallback(() => {
    if (sessionState !== 'pending' || !pendingRequest) {
      logger.warn('Não há solicitação pendente para aceitar');
      return;
    }

    logger.info('Aceitando solicitação de visualização');
    signaling.sendAccept();
    setSessionState('connecting');
  }, [sessionState, pendingRequest, signaling]);

  // Recusa solicitação
  const rejectRequest = useCallback((reason?: string) => {
    if (sessionState !== 'pending') {
      logger.warn('Não há solicitação pendente para recusar');
      return;
    }

    logger.info('Recusando solicitação de visualização', { reason });
    signaling.sendReject(reason);
    setPendingRequest(null);
    setSessionState('idle');
  }, [sessionState, signaling]);

  // Encerra sessão
  const endSession = useCallback(() => {
    logger.info('Encerrando sessão de compartilhamento');
    signaling.sendEnd('client', 'user_action');
    screenShare.stopSharing();
    webrtc.close();
    setPendingRequest(null);
    setSessionState('ended');
    pendingIceCandidatesRef.current = [];
  }, [signaling, screenShare, webrtc]);

  // Cleanup quando stream é encerrado externamente (botão nativo do navegador)
  useEffect(() => {
    if (!screenShare.isSharing && sessionState === 'connected') {
      logger.info('Compartilhamento encerrado pelo usuário');
      signaling.sendEnd('client', 'user_action');
      webrtc.close();
      setSessionState('ended');
    }
  }, [screenShare.isSharing, sessionState, signaling, webrtc]);

  // Reset ao mudar de conversa
  useEffect(() => {
    return () => {
      if (sessionState !== 'idle' && sessionState !== 'ended') {
        screenShare.stopSharing();
        webrtc.close();
      }
      setSessionState('idle');
      setPendingRequest(null);
      setError(null);
      pendingIceCandidatesRef.current = [];
    };
  }, [conversationId]);

  // Reset para idle depois de ended
  useEffect(() => {
    if (sessionState === 'ended') {
      const timer = setTimeout(() => {
        setSessionState('idle');
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionState]);

  return {
    sessionState,
    pendingRequest,
    isSharing: screenShare.isSharing,
    acceptRequest,
    rejectRequest,
    endSession,
    error,
  };
}
